import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import IProduct from "../../entities/IProduct";
import { IMedia } from "../../entities/IMedia";
import ProductService from "../../services/ProductService";
import { getErrorMessage, getMediaSrc, resolveImageUrl, unwrapData } from "../../services/apiUtils";
import { theme } from "../../theme";

const sectionStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow:
    "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  overflow: "hidden",
  mb: 2,
};

const headerStyle = {
  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
  color: "#fff",
  px: 2.5,
  py: 1.75,
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

type Props = IProduct & {

  onThumbnailSaved?: (thumbnailUrl: string) => void;
  onGalleryChange?: (mediaList: Array<IMedia>) => void;
};

export const ProductImagesProgressBarForm = (props: Props) => {
  const { onThumbnailSaved, onGalleryChange, ...product } = props;

  const [thumbnailUrl, setThumbnailUrl] = useState<string>(product.thumbnail || "");
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const [gallery, setGallery] = useState<Array<IMedia>>(
    (product.mediaList as Array<IMedia>) || []
  );
  const [galleryUploading, setGalleryUploading] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setThumbnailUrl(product.thumbnail || "");
  }, [product.thumbnail]);

  useEffect(() => {
    setGallery((product.mediaList as Array<IMedia>) || []);

  }, [product.productId]);

  const notifyError = (err: any, fallback: string) => {
    setSnackbar({ open: true, message: getErrorMessage(err, fallback), severity: "error" });
  };

  const handleThumbnailSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setThumbnailUploading(true);
    ProductService.upload(file, product.productId, () => {})
      .then((res: any) => {
        const uploaded = unwrapData(res) as IMedia;
        const url = uploaded?.col1 || "";
        if (!url) throw new Error("Upload did not return an image URL.");
        return ProductService.updateThumbnail(product.productId, url).then(() => {
          setThumbnailUrl(url);
          onThumbnailSaved?.(url);
          setSnackbar({ open: true, message: "Thumbnail updated.", severity: "success" });
        });
      })
      .catch((err: any) => notifyError(err, "Could not upload the thumbnail."))
      .finally(() => setThumbnailUploading(false));
  };

  const handleGallerySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = "";
    if (files.length === 0) return;

    setGalleryUploading(true);
    Promise.all(
      files.map((file) =>
        ProductService.upload(file, product.productId, () => {}).then(
          (res: any) => unwrapData(res) as IMedia
        )
      )
    )
      .then((uploaded) => {
        const next = [...gallery, ...uploaded.filter(Boolean)];
        setGallery(next);
        onGalleryChange?.(next);
        setSnackbar({ open: true, message: "Images uploaded.", severity: "success" });
      })
      .catch((err: any) => notifyError(err, "Some images could not be uploaded."))
      .finally(() => setGalleryUploading(false));
  };

  const handleRemoveGalleryImage = (mediaId: number) => {
    ProductService.deleteMedia(mediaId)
      .then(() => {
        const next = gallery.filter((img) => img.mediaId !== mediaId);
        setGallery(next);
        onGalleryChange?.(next);
      })
      .catch((err: any) => notifyError(err, "Could not remove this image."));
  };

  const handleUseAsThumbnail = (media: IMedia) => {
    const url = media.col1 || media.dataUrl || "";
    if (!url) return;
    ProductService.updateThumbnail(product.productId, url)
      .then(() => {
        setThumbnailUrl(url);
        onThumbnailSaved?.(url);
        setSnackbar({ open: true, message: "Thumbnail updated.", severity: "success" });
      })
      .catch((err: any) => notifyError(err, "Could not set this as the thumbnail."));
  };

  return (
    <Box>
      <Box sx={sectionStyle}>
        <Box sx={headerStyle}>
          <ImageOutlinedIcon />
          <Typography variant="subtitle1" fontWeight={600}>
            Thumbnail (Main Image)
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is the primary image shown for the product everywhere in the storefront. Upload it first.
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box
              sx={{
                width: 140,
                height: 140,
                borderRadius: "12px",
                border: `1px dashed ${theme.palette.grey?.[400] || "#ccc"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                bgcolor: "#faf9fc",
                flexShrink: 0,
              }}
            >
              {thumbnailUploading ? (
                <CircularProgress size={28} />
              ) : thumbnailUrl ? (
                <img
                  src={resolveImageUrl(thumbnailUrl)}
                  alt="Thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ImageOutlinedIcon sx={{ color: "#ccc", fontSize: 40 }} />
              )}
            </Box>
            <Box>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadRoundedIcon />}
                disabled={thumbnailUploading}
                sx={{ borderRadius: "10px" }}
              >
                {thumbnailUrl ? "Replace Thumbnail" : "Upload Thumbnail"}
                <input type="file" hidden accept="image/*" onChange={handleThumbnailSelect} />
              </Button>
              {!thumbnailUrl && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  A thumbnail is required before you publish this product.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={sectionStyle}>
        <Box sx={{ ...headerStyle, justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <PhotoLibraryOutlinedIcon />
            <Typography variant="subtitle1" fontWeight={600}>
              Gallery ({gallery.length})
            </Typography>
          </Box>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={galleryUploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadRoundedIcon />}
            disabled={galleryUploading}
            sx={{ borderRadius: "10px", color: "#fff", borderColor: "rgba(255,255,255,0.7)" }}
          >
            {galleryUploading ? "Uploading..." : "Add Images"}
            <input type="file" hidden multiple accept="image/*" onChange={handleGallerySelect} />
          </Button>
        </Box>
        <Box sx={{ p: 2.5 }}>
          {gallery.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No gallery images yet. Add a few extra angles or lifestyle shots — these are what
              shoppers browse in the product's photo carousel.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {gallery.map((img) => {
                const isThumb = !!thumbnailUrl && (img.col1 || img.dataUrl) === thumbnailUrl;
                return (
                  <Grid item xs={6} sm={4} md={3} key={img.mediaId}>
                    <Box
                      sx={{
                        position: "relative",
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${theme.palette.grey?.[300] || "#eee"}`,
                        height: 120,
                      }}
                    >
                      <img
                        src={getMediaSrc(img)}
                        alt={img.filename || "Product image"}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      {isThumb && (
                        <Chip
                          size="small"
                          icon={<StarRoundedIcon sx={{ color: "#fff !important" }} />}
                          label="Thumbnail"
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        {!isThumb && (
                          <Tooltip title="Use as thumbnail">
                            <IconButton
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.85)" }}
                              onClick={() => handleUseAsThumbnail(img)}
                            >
                              <StarRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove image">
                          <IconButton
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.85)" }}
                            onClick={() => handleRemoveGalleryImage(img.mediaId)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
